/* eslint-disable camelcase */
import React, { useState, useContext } from "react"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
} from "@mui/material"
import { AccountBalanceWallet, PhoneInTalk, Speed, TrendingUp, Dashboard as DashboardIcon } from "@mui/icons-material"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf" // Icono para el botón PDF
import { PieChart } from "@mui/x-charts/PieChart"
import { BarChart } from "@mui/x-charts/BarChart"
import dayjs from "dayjs"

// Importaciones para generación del PDF en Frontend
import JsPDF from "jspdf"
import autoTable from "jspdf-autotable"

// Importaciones estándar de su arquitectura SIAC
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomDatePicker from "../../components/CustomDatePicker"
import { GlobalContext } from "../../contexts/GlobalContext"
import { useQuery, api } from "../../api"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(3),
  flexGrow: 1,
  padding: "0 16px",
  paddingBottom: "40px",
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

// Utilidad para formatear moneda
const formatMoneda = (valor) => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(valor || 0)
}

// --- COMPONENTE REUTILIZABLE PARA LAS TARJETAS KPI ---
const KPICard = ({ title, value, icon, color, subtitle }) => (
  <Card elevation={2} sx={{ height: "100%", borderLeft: `6px solid ${color}`, borderRadius: 2 }}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            variant="body2"
            color="textSecondary"
            fontWeight="bold"
            sx={{ textTransform: "uppercase", mb: 1 }}
          >
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ bgcolor: `${color}1A`, p: 1.5, borderRadius: "50%", display: "flex" }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
)

const DashboardCarteraIndex = () => {
  // Extraemos el contexto global completo para obtener nombre de Cía y el Usuario
  const globalContext = useContext(GlobalContext)
  const usuarioSesion =
    globalContext?.user || globalContext?.seleccion?.user || localStorage.getItem("cliciausu") || "USUARIO DESCONOCIDO"

  // Filtros: Por defecto evaluamos los últimos 30 días hasta hoy
  const [fecIni, setFecIni] = useState(dayjs().subtract(30, "day"))
  const [fecFin, setFecFin] = useState(dayjs())
  const [recaudadorFiltro, setRecaudadorFiltro] = useState("TODOS")

  // 1. OBTENER CATÁLOGO DE OFICIALES / RECAUDADORES
  const { data: recaudadores = [], isLoading: loadingOfi } = useQuery({
    queryKey: ["getRecaudadoresDashboardCartera"],
    queryFn: async () => {
      try {
        const payload = {
          page: 1,
          perPage: 1000,
          filters: { usrstatus: "A", usrflagoficre: -1 },
        }
        const res = await api.post("/CreacionUsuarios/getAllUsuarios", payload)

        let dataArray = []
        if (Array.isArray(res.data)) dataArray = res.data
        else if (Array.isArray(res.data?.data)) dataArray = res.data.data
        else if (Array.isArray(res.data?.data?.data)) dataArray = res.data.data.data

        return dataArray
      } catch (error) {
        console.error("Error al cargar oficiales:", error)
        return []
      }
    },
    refetchOnWindowFocus: false,
  })

  // 2. OBTENER MÉTRICAS DEL DASHBOARD
  const { data: metrics, isLoading: loadingMetrics } = useQuery({
    queryKey: ["getDashboardCobranzas", fecIni, fecFin, recaudadorFiltro],
    queryFn: async () => {
      const payload = {
        filtros: {
          fecIni: fecIni ? dayjs(fecIni).format("YYYY-MM-DD") : "",
          fecFin: fecFin ? dayjs(fecFin).format("YYYY-MM-DD") : "",
          recaudador: recaudadorFiltro === "TODOS" ? "" : recaudadorFiltro,
        },
      }
      const res = await api.post("/AsignaciondeCartera/getDashboardCobranzas", payload)
      return res.data
    },
    refetchOnWindowFocus: false,
    enabled: !!fecIni && !!fecFin,
  })

  const isLoading = loadingOfi || loadingMetrics

  const kpis = metrics?.kpis || {
    total_asignado: 0,
    total_recuperado: 0,
    saldo_pendiente: 0,
    efectividad_pct: 0,
    hit_rate_pct: 0,
    velocidad_cierre_dias: 0,
    total_facturas_asignadas: 0,
  }
  const agingData = metrics?.charts?.aging || []
  const leaderboard = metrics?.leaderboard || []

  const pieData = [
    { id: 0, value: kpis.total_recuperado, label: "Recuperado (Éxito)", color: "#2E7D32" },
    { id: 1, value: kpis.saldo_pendiente, label: "Pendiente (Riesgo)", color: "#d32f2f" },
  ]

  // ==========================================================================
  // LÓGICA DE EXPORTACIÓN A PDF DEL LEADERBOARD
  // ==========================================================================
  const exportarPDFLeaderboard = () => {
    if (leaderboard.length === 0) return

    const doc = new JsPDF("p", "pt", "a4") // Portrait (Vertical)

    // Extracción dinámica del nombre de la compañía integrando LocalStorage como prioridad de respaldo
    const nombreCia =
      globalContext?.ciaSeleccionada?.label ||
      globalContext?.ciaSeleccionada?.cianombre ||
      globalContext?.empresaSeleccionada?.label ||
      globalContext?.seleccion?.cianombre ||
      localStorage.getItem("ciaalias") ||
      "EMPRESA NO DEFINIDA"

    // --- ENCABEZADO INSTITUCIONAL (Formato Estado de Cuenta) ---
    doc.setFontSize(16)
    doc.setTextColor(25, 108, 135) // #196C87 (Color Primary del Theme)
    doc.text(nombreCia, 40, 40)

    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text("RANKING DE EFECTIVIDAD DE COBRANZAS", 40, 65)

    doc.setFontSize(10)
    doc.setTextColor(60, 60, 60)

    const txtRecaudador =
      recaudadorFiltro === "TODOS"
        ? "Todos los Oficiales"
        : recaudadores.find((r) => r.usrcodigo === recaudadorFiltro)?.usrnombre || recaudadorFiltro

    doc.text(`Oficial Evaluado: ${txtRecaudador}`, 40, 85)
    doc.text(`Período: ${dayjs(fecIni).format("DD/MM/YYYY")} al ${dayjs(fecFin).format("DD/MM/YYYY")}`, 40, 100)
    doc.text(`Fecha de Impresión: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 40, 115)

    // --- TABLA DE DATOS (LEADERBOARD) ---
    const tableColumn = ["Posición", "Recaudador / Oficial", "Asignado", "Recuperado", "Hit Rate", "Efectividad"]
    const tableRows = []

    leaderboard.forEach((row, index) => {
      tableRows.push([
        String(index + 1),
        row.recaudador,
        formatMoneda(row.asignado),
        formatMoneda(row.recuperado),
        `${row.hit_rate_pct}%`,
        `${row.efectividad_pct}%`,
      ])
    })

    autoTable(doc, {
      startY: 135,
      head: [tableColumn],
      body: tableRows,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 5, textColor: [40, 40, 40] },
      headStyles: { fillColor: [25, 108, 135], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: {
        0: { halign: "center", cellWidth: 50 }, // Posición
        1: { halign: "left" }, // Nombre
        2: { halign: "right" }, // Asignado
        3: { halign: "right", fontStyle: "bold", textColor: [46, 125, 50] }, // Recuperado (Verde)
        4: { halign: "center" }, // Hit Rate
        5: { halign: "center", fontStyle: "bold" }, // Efectividad
      },
      // --- HOOK PARA IMPRIMIR USUARIO Y PÁGINA EN TODAS LAS HOJAS ---
      didDrawPage: function (data) {
        // Obtenemos las dimensiones de la página actual
        const pageSize = doc.internal.pageSize
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()

        doc.setFontSize(8)
        doc.setTextColor(150, 150, 150) // Gris claro

        // Imprime "Generado por: USUARIO" en la esquina inferior izquierda
        doc.text(`Generado por: ${usuarioSesion}`, data.settings.margin.left, pageHeight - 20)

        // Imprime "Página X" en la esquina inferior derecha
        const str = `Página ${doc.internal.getNumberOfPages()}`
        doc.text(str, pageWidth - data.settings.margin.right, pageHeight - 20, { align: "right" })
      },
    })

    doc.save(`Ranking_Efectividad_${dayjs().format("YYYYMMDD")}.pdf`)
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* TÍTULO */}
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1, color: "#196C87" }}>
          <DashboardIcon sx={{ fontSize: 36, mr: 1 }} />
          <Typography variant="h4" fontWeight="bold">
            Efectividad de Cobranzas
          </Typography>
        </Box>
        <Typography variant="body1" align="center" color="textSecondary" mb={4}>
          Evaluación de rendimiento del equipo de recaudación y análisis de recuperación de cartera
        </Typography>

        <CustomBackdrop isLoading={isLoading} />

        <StyledRoot>
          {/* PANEL DE FILTROS */}
          <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 2, borderTop: "4px solid #196C87" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <CustomDatePicker label="Asignado Desde" value={fecIni} setValue={setFecIni} format="DD/MM/YYYY" />
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <CustomDatePicker label="Asignado Hasta" value={fecFin} setValue={setFecFin} format="DD/MM/YYYY" />
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Oficial / Recaudador"
                  value={recaudadorFiltro}
                  onChange={(e) => setRecaudadorFiltro(e.target.value)}
                  size="small"
                >
                  <MenuItem value="TODOS">Todos los Recaudadores</MenuItem>
                  {recaudadores.map((rec) => (
                    <MenuItem key={rec.usrcodigo} value={rec.usrcodigo}>
                      {rec.usrnombre} ({rec.usrcodigo})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary" align="right">
                  Última evaluación: {dayjs().format("DD/MM/YYYY HH:mm")}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* SECCIÓN DE MÉTRICAS PRINCIPALES (KPIs) */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Cartera Asignada"
                value={formatMoneda(kpis.total_asignado)}
                icon={<AccountBalanceWallet />}
                color="#196C87"
                subtitle={`${kpis.total_facturas_asignadas} facturas en gestión`}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Efectividad (Recuperado)"
                value={`${kpis.efectividad_pct}%`}
                icon={<TrendingUp />}
                color={kpis.efectividad_pct >= 50 ? "#2E7D32" : "#ed6c02"}
                subtitle={formatMoneda(kpis.total_recuperado)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Tasa de Contacto (Hit Rate)"
                value={`${kpis.hit_rate_pct}%`}
                icon={<PhoneInTalk />}
                color="#0288d1"
                subtitle="Cuentas con al menos 1 abono"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Velocidad de Cierre"
                value={`${kpis.velocidad_cierre_dias} Días`}
                icon={<Speed />}
                color="#9c27b0"
                subtitle="Promedio para liquidar factura"
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* SECCIÓN DE GRÁFICOS Y TABLAS */}
          <Grid container spacing={3}>
            {/* Gráfico 1: Embudo de Dinero (Dona) */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
              >
                <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom align="center">
                  Embudo de Recuperación
                </Typography>
                <Box
                  sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 250 }}
                >
                  {kpis.total_asignado === 0 ? (
                    <Typography color="textSecondary">Sin datos en el período</Typography>
                  ) : (
                    <PieChart
                      series={[
                        {
                          data: pieData,
                          highlightScope: { faded: "global", highlighted: "item" },
                          faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                          innerRadius: 60,
                          paddingAngle: 2,
                          cornerRadius: 4,
                          valueFormatter: (v) => formatMoneda(v.value),
                        },
                      ]}
                      height={250}
                      slotProps={{
                        legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" } },
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Gráfico 2: Desempeño por Antigüedad de la Deuda (Aging) */}
            <Grid item xs={12} md={8}>
              <Paper
                elevation={2}
                sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
              >
                <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom align="center">
                  Gestión por Antigüedad de la Deuda (Aging)
                </Typography>
                <Box
                  sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 250 }}
                >
                  {agingData.length === 0 ? (
                    <Typography color="textSecondary">Sin datos en el período</Typography>
                  ) : (
                    <BarChart
                      xAxis={[{ scaleType: "band", data: agingData.map((d) => d.rango) }]}
                      series={[
                        {
                          data: agingData.map((d) => d.recuperado),
                          label: "Recuperado",
                          color: "#2E7D32",
                          valueFormatter: (v) => formatMoneda(v),
                        },
                        {
                          data: agingData.map((d) => d.pendiente),
                          label: "Pendiente",
                          color: "#d32f2f",
                          valueFormatter: (v) => formatMoneda(v),
                        },
                      ]}
                      height={300}
                      margin={{ top: 20, bottom: 40, left: 70, right: 20 }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Tabla 3: Ranking de Recaudadores (Leaderboard) */}
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                {/* CABECERA DE LA TABLA CON BOTÓN PDF */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Ranking de Efectividad (Leaderboard)
                  </Typography>
                  <Tooltip title="Exportar Ranking a PDF">
                    <span>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={exportarPDFLeaderboard}
                        disabled={leaderboard.length === 0}
                        sx={{ fontWeight: "bold" }}
                      >
                        Exportar PDF
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                <TableContainer>
                  <Table size="small" sx={{ minWidth: 650 }}>
                    <TableHead sx={{ backgroundColor: "#f5f7fa" }}>
                      <TableRow>
                        <TableCell>
                          <b>Posición</b>
                        </TableCell>
                        <TableCell>
                          <b>Recaudador / Oficial</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Asignado (USD)</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Recuperado (USD)</b>
                        </TableCell>
                        <TableCell align="center">
                          <b>Hit Rate (%)</b>
                        </TableCell>
                        <TableCell align="center">
                          <b>Efectividad (%)</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leaderboard.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No hay gestiones en el rango seleccionado.
                          </TableCell>
                        </TableRow>
                      ) : (
                        leaderboard.map((row, index) => (
                          <TableRow key={row.recaudador} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                            <TableCell>
                              {index === 0 ? "🥇 1" : index === 1 ? "🥈 2" : index === 2 ? "🥉 3" : index + 1}
                            </TableCell>
                            <TableCell>{row.recaudador}</TableCell>
                            <TableCell align="right">{formatMoneda(row.asignado)}</TableCell>
                            <TableCell align="right" sx={{ color: "#2E7D32", fontWeight: "bold" }}>
                              {formatMoneda(row.recuperado)}
                            </TableCell>
                            <TableCell align="center">{row.hit_rate_pct}%</TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  bgcolor: row.efectividad_pct >= 50 ? "#e8f5e9" : "#fff3e0",
                                  color: row.efectividad_pct >= 50 ? "#2e7d32" : "#e65100",
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontWeight: "bold",
                                  display: "inline-block",
                                }}
                              >
                                {row.efectividad_pct}%
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </StyledRoot>
      </div>
    </ThemeProvider>
  )
}

export default DashboardCarteraIndex
